import React from 'react';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight } from 'lucide-react';

/**
 * QuoteCTABlock — embedded inline CTA card linking to /compare-pricing.
 * Drop onto any page (Sprint 1/2/3 service pages, homepage, blog).
 *
 * Props:
 *   headline    — override default "Not sure which package fits..."
 *   subtext     — supporting copy
 *   buttonLabel — CTA label (default "Get My Quote")
 *   variant     — 'default' | 'compact' | 'inline'
 */
const QuoteCTABlock = ({
  headline = 'Not sure which package fits your business?',
  subtext = 'Compare pricing and generate a custom proposal in under 60 seconds.',
  buttonLabel = 'Get My Quote',
  variant = 'default',
}) => {
  if (variant === 'inline') {
    return (
      <div className="my-8 p-5 bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50 border border-rose-200 rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="quote-cta-inline">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white rounded-lg shadow-sm flex-shrink-0">
            <Calculator className="w-5 h-5 text-rose-600" />
          </div>
          <div>
            <div className="font-bold text-slate-900 text-base">{headline}</div>
            <div className="text-sm text-slate-600">{subtext}</div>
          </div>
        </div>
        <Link
          to="/compare-pricing"
          className="px-5 py-2.5 bg-rose-600 text-white font-bold rounded-lg whitespace-nowrap hover:bg-rose-700 transition-colors flex items-center gap-1.5 shadow-md"
          data-testid="quote-cta-inline-link"
        >
          {buttonLabel} <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="my-12 px-6 py-10 bg-gradient-to-br from-rose-600 via-rose-700 to-orange-700 rounded-2xl text-center shadow-xl" data-testid="quote-cta-default">
      <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full mb-3">
        <Calculator className="w-4 h-4 text-white" />
        <span className="text-xs uppercase tracking-wider font-bold text-white">60-second quote</span>
      </div>
      <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{headline}</h3>
      <p className="text-base text-white/90 mb-6 max-w-2xl mx-auto">{subtext}</p>
      <Link
        to="/compare-pricing"
        className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-rose-700 font-bold rounded-xl hover:bg-slate-50 transition-colors shadow-lg"
        data-testid="quote-cta-default-link"
      >
        {buttonLabel} <ArrowRight className="w-5 h-5" />
      </Link>
    </div>
  );
};

export default QuoteCTABlock;
